<template>
  <div>

    <form
      v-if="isLoadedNormal && contest !== null"
      @submit.prevent="updateContestWithConfirmation"
    >
      <div class="form-group">
        <label for="player-group-selector">Segment</label>
        <b-form-select
          id="player-group-selector"
          v-model="contest.playerGroupId"
          :options="playerGroupOptions"
        />
      </div>

      <hr>

      <template v-if="isClientMinVersionSupported">
        <b-form-group
          label="Client min version"
          label-for="client-min-version"
          label-class="mb-1"
        >
          <input id="client-min-version" class="form-control" v-model="clientMinVersionStr" required>
        </b-form-group>

        <hr>
      </template>

      <div class="mb-4">
        <h5>Start</h5>

        <b-form-datepicker
          v-model="plannedStartDateStr"
          :min="minPlannedStartDateStr"
          :start-weekday="MONDAY"
          :readonly="isStimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedStartTimeStr"
          :readonly="isStimeReadonly"
        />
      </div>

      <div class="mb-4">
        <h5>Finish</h5>

        <b-form-datepicker
          v-model="plannedFinishDateStr"
          :min="minPlannedFinishDateStr"
          :start-weekday="MONDAY"
          :readonly="isFtimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedFinishTimeStr"
          :readonly="isFtimeReadonly"
        />
      </div>

      <div class="mb-3">
        <h5>Duration</h5>
        <p>
          {{durationStr}}
        </p>
      </div>

      <hr>

      <b-button :to="{name: 'contest_info', params: {contestId: contestId}}">Cancel</b-button>

      <b-button
        type="submit"
        :disabled="isReadonly"
        variant="primary"
      >
        Save
      </b-button>

    </form>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { getPlayerGroupOptionsVerbose } from '../model/event.js';

/**
 * @typedef { import('../model/contest.js').Contest } Contest
 */

import { isContestEditable           } from '../model/contest.js';
import { isContestStimeEditable      } from '../model/contest.js';
import { isContestFtimeEditable      } from '../model/contest.js';
import { isClientMinVersionSupported } from '../model/contest.js';

import { confirmAction                       } from '../service/confirm.js';
import { convertStampToDateStr               } from '../util.js';
import { convertStampToTimeStr               } from '../util.js';
import { convertMomentToDateStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { convertVersionCodeToStr             } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput        from './TimeInput.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'ContestEdit',

  components: {
    LoadingIndicator,
    TimeInput,
  },

  props: {
    contestId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,

      /** @type {?Contest} */
      contest: null,

      playerGroupOptions: getPlayerGroupOptionsVerbose(),

      plannedStartDateStr: '',
      minPlannedStartDateStr: '',
      plannedStartTimeStr: '',

      plannedFinishDateStr: '',
      minPlannedFinishDateStr: '',
      plannedFinishTimeStr: '',

      clientMinVersionStr: '',
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.contest === null) {
        return `Contest with id ${this.contestId} does not exist.`;
      }
      return null;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDateStr, this.plannedStartTimeStr);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDateStr, this.plannedFinishTimeStr);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {boolean} */
    isReadonly() {
      const contest = this.contest;
      if(contest === null) {
        return true;
      }

      const isEditable = isContestEditable(contest.status);
      return !isEditable;
    },

    /** @returns {boolean} */
    isStimeEditable() {
      const contest = this.contest;
      if(contest === null) {
        return false; // data not loaded
      }

      return isContestStimeEditable(contest.status);
    },

    /** @returns {boolean} */
    isStimeReadonly() {
      return !this.isStimeEditable;
    },

    /** @returns {boolean} */
    isFtimeEditable() {
      const contest = this.contest;
      if(contest === null) {
        return false;
      }

      return isContestFtimeEditable(contest.status);
    },

    /** @returns {boolean} */
    isFtimeReadonly() {
      return !this.isFtimeEditable;
    },

    /** @returns {boolean} */
    isClientMinVersionSupported() {
      const contest = this.contest;
      if(contest === null) {
        return false;
      }

      return isClientMinVersionSupported(contest.type);
    },
  },

  created() {
    this.loadContest();
  },

  methods: {
    loadContest() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/contest/${this.contestId}/info`;
      HTTP.get(url)
      .then(response => {
        const contest = response.data.data;
        this.contest = contest;

        const now = new Date();
        const nowDateStr = convertMomentToDateStr(now);

        this.plannedStartDateStr     = convertStampToDateStr(contest.stime);
        this.minPlannedStartDateStr  = nowDateStr;
        this.plannedStartTimeStr     = convertStampToTimeStr(contest.stime)

        this.plannedFinishDateStr    = convertStampToDateStr(contest.ftime);
        this.minPlannedFinishDateStr = nowDateStr;
        this.plannedFinishTimeStr    = convertStampToTimeStr(contest.ftime);

        this.clientMinVersionStr     = convertVersionCodeToStr(contest.clientMinVersionCode);

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e, {sender: this});
      });
    },

    updateContestWithConfirmation() {
      this.confirmContestUpdate(() => {
        this.updateContest();
      });
    },

    // function onConfirmed()
    confirmContestUpdate(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to save changes for this contest {env}?',
        'Save',
        onConfirmed,
      );
    },

    updateContest() {
      notifier.deleteSenderNotifications(this);

      const plannedStartSec  = Math.floor(this.plannedStartMoment.getTime() / 1000);
      const plannedFinishSec = Math.floor(this.plannedFinishMoment.getTime() / 1000);

      const data = new FormData();
      data.set('stime', plannedStartSec);
      data.set('ftime', plannedFinishSec);
      data.set('player_group_id', this.contest.playerGroupId);

      if(this.isClientMinVersionSupported) {
        const trimmedClientMinVersion = this.clientMinVersionStr.trim();
        if(trimmedClientMinVersion.length === 0) {
          notifier.showError('Client min version must be specified.', {sender: this});
          return;
        }

        data.set('client_min_version', trimmedClientMinVersion);
      }

      const url = `/contest/${this.contestId}`;
      HTTP.post(url, data)
      .then(() => {
        notifier.showSuccessAfterTransition('Changes saved.');
        this.$router.push({name: 'contest_info', params: {contestId: this.contestId}});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};
</script>
